import _ from 'lodash';
import { Reducer } from 'redux';
import { SearchDataItem } from '../../../logic/domains/search.domain';
import { SearchFilterValues } from '../../../logic/domains/searchFilters.domain';
import formatAsUpperFirst from '../../../logic/functions/common/formatAsUpperFirst';
import {
	QuickSearchActionTypes,
	SearchAction,
} from '../../actions/search.data.actions';
import {
	SearchFilterActions,
	SearchFilterActionTypes,
} from '../../actions/search.filter.actions';
import { countFilters } from './filters/countFilters';

export type SearchState = {
	totalCount: number;
	searchData: Array<SearchDataItem>;
	filteredData?: Array<SearchDataItem>;
	searchFilters: SearchFilterValues;
	error: boolean;
	loading: boolean;
	filterCount: number;
	errorMsg?: string;
};

const initialState: SearchState = {
	totalCount: 0,
	searchData: [],
	filteredData: undefined,
	error: false,
	loading: true,
	searchFilters: {
		keyword: '',
		accountType: ['all'],
		ageMin: '',
		ageMax: '',
		postcode: '',
		advisedPortfolioMin: '',
		advisedPortfolioMax: '',
		investmentType: ['all'],
		investmentOptions: [],
		insuranceType: '',
		insuranceAmountMin: '',
		insuranceAmountMax: '',
		annualPremiumMin: '',
		annualPremiumMax: '',
		insuranceAnniversary: '',
		insuranceOptions: [],
		policyStatus: '',
		includeClosedAccounts: false,
		showLinkedAccountsOnly: false,
	},
	filterCount: 0,
};

function transformCsvToJson(data: string[]): SearchDataItem[] {
	const response: SearchDataItem[] = _(data)
		.map(item => {
			return _(item)
				.split(',')
				.compact()
				.zip()
				.flatten()
				.value();
		})
		.map(items => {
			if (items[0] === 'C') {
				const accountList = _.takeRight(items, items.length - 4);
				return {
					type: 'client',
					id: items[1],
					name: formatAsUpperFirst(items[2] as string),
					token: items[3] as string,
					accountIdList: _.sortBy(accountList),
				} as SearchDataItem;
			}
			if (items[0] === 'P') {
				return {
					type: 'plan',
					id: items[1],
					token: items[3],
					name: formatAsUpperFirst(items[2] as string),
				} as SearchDataItem;
			}
		})
		.value() as SearchDataItem[];
	return response;
}

type Actions = SearchAction | SearchFilterActions;
const reducer: Reducer<SearchState, Actions> = (
	state = initialState,
	action: Actions
): SearchState => {
	switch (action.type) {
		case QuickSearchActionTypes.QUICK_SEARCH_RESULTS_SUCCESS: {
			const searchData = _(transformCsvToJson(action.response))
				.flatten()
				.concat()
				.sortBy('name')
				.value();

			return {
				...state,
				searchData,
				totalCount: searchData.length,
				loading: false,
				error: false,
			};
		}
		case QuickSearchActionTypes.QUICK_SEARCH_RESULTS_FAILURE: {
			return {
				...state,
				searchData: [],
				error: true,
				loading: false,
			};
		}
		case QuickSearchActionTypes.QUICK_SEARCH_RESULTS_LOADING: {
			return {
				...state,
				loading: true,
			};
		}
		case SearchFilterActionTypes.SEARCH_APPLY_FILTERS: {
			const searchFilters = action.searchFilters || state.searchFilters;
			const searchKeyword = action.searchKeyword || '';
			let appliedFilterCount = initialState.filterCount;

			appliedFilterCount = countFilters(
				initialState.searchFilters,
				searchFilters
			);
			return {
				...state,
				searchFilters: {
					...searchFilters,
					keyword: searchKeyword,
				},
				filterCount: appliedFilterCount,
			};
		}
		case SearchFilterActionTypes.CLEAR_SEARCH_FILTERS: {
			return {
				...state,
				searchFilters: {
					...initialState.searchFilters,
				},
				filterCount: 0,
				filteredData: undefined,
			};
		}
		default: {
			return state;
		}
	}
};

export const escapeRegex = (value: string) => {
	return value.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');
};

export { initialState, reducer as searchReducer };
